import { createSelector } from 'reselect';
import dayjs from 'dayjs';

/**
 * Direct selector to the app state domain
 */
const selectAppDomain = () => state => state.app;

const selectUser= () => createSelector(
  selectAppDomain(),
  substate => substate.user
);

/**
 * Other specific selectors
 */

const selectAuthToken = () => createSelector(
  selectAppDomain(),
  substate => substate.user && substate.user.authToken
);

const selectEnterpriseAdmins = () => createSelector(
  selectAppDomain(),
  substate => substate.user && substate.user.enterprise_admins
);

const selectEmail = () => createSelector(
  selectAppDomain(),
  substate => substate.user && substate.user.email
);

const selectIsPhotographer = () => createSelector(
  selectAppDomain(),
  substate => substate.user && substate.user.permission_role === 'photographer'
);

const selectPermissionRole = () => createSelector(
  selectAppDomain(),
  substate => substate.user && substate.user.permission_role
);

// Whether the account's subscription has ended, so channel CREATION and EDITING
// are blocked (viewing — galleries, leads, downloads — is not).
//
// `=== true` is the fail-open mechanism, not a style preference. The flag is
// served by movebooth-rails on the v1/v2 user payload; an older backend omits it
// entirely, and a truthiness check would then be answering "is this key set?"
// rather than "did the server say blocked?". Only an explicit `true` gates
// anything, so a dashboard talking to a backend that predates the flag behaves
// exactly as it did before. Enforcement itself is server-side (ChannelPolicy);
// this only decides which control the user is shown.
const selectChannelManagementBlocked = () => createSelector(
  selectAppDomain(),
  substate => substate.user && substate.user.channel_management_blocked === true
);

// Whether the session fetch has landed — i.e. whether an absent field means
// "the server said no" or merely "we have not asked yet".
//
// On reload the app saga restores the auth token from localStorage and puts
// SET_AUTH_TOKEN synchronously, then FORKS getSessionFlow; SET_USER lands only
// when that request resolves. Until then `state.app.user` is the reducer's
// initialState — not empty, but every real field blank (`id: null`,
// `email: ''`). `id` is the identifying field: nothing sets it but a successful
// session response.
//
// This is the necessary companion to the fail-open read above. Fail-open is
// right for an OLD BACKEND that will never send the field, and wrong for a
// backend that simply has not answered yet — the two are indistinguishable from
// the field alone, so a gated route has to ask this first.
//
// Only the two gated ROUTES wait on it, and that is sufficient rather than
// partial: every control whose behaviour depends on the block flag navigates to
// /channels/new or /channels/:id/edit, both of which wait here, so no gated
// action can COMPLETE during the pending window. The row menu's archive and
// delete act in place without a route, but they never consult this flag at all
// — they render from the server-authoritative per-row `can_archive`/`can_delete`
// on the list payload — so they behave identically before and after the session
// lands, and the window cannot make them diverge.
const selectSessionUserLoaded = () => createSelector(
  selectAppDomain(),
  substate => Boolean(substate.user && substate.user.id)
);

// The session restore failed transiently (see the app reducer). Distinct from
// "not loaded yet": one resolves on its own, the other never will.
const selectSessionLoadFailed = () => createSelector(
  selectAppDomain(),
  substate => substate.sessionLoadFailed === true
);

// Whole days left in a TRIAL (self-serve or hardware), or `null` when there is
// no honest number to show — not trialing, session not landed, or no usable end
// date on the payload. `null` means "render nothing"; a countdown is only worth
// showing when it can name a real day.
//
// Only the day count is derived here; the date itself is the server's, read
// from `trial_end_date` (see the comment on the read below). The other field
// that reads like it answers this does not: `User#remaining_days_in_renewel`
// returns nil unless `is_lite_photographer?`, so it is nil for exactly the Pro
// hardware trials this counts down.
//
// `trial_end_date` is a `format: date` field, so it arrives as a bare
// `YYYY-MM-DD`. dayjs parses that as LOCAL midnight, where `new Date()`
// would read it as UTC and shift the answer by a day for anyone west of
// Greenwich. Counting in the customer's own calendar days is the only reading
// of "ends in 3 days" they can check against their own calendar.
//
// Every guard below is load-bearing rather than defensive habit: the field is
// null for every account not on a trial — the common case — and an absent or
// null date must read as "render nothing", never as a trial ending today (the
// `dayjs(undefined)`-is-now trap guarded at the read).
//
// Memoization: the count is computed against `dayjs()` at call time but cached
// against the app slice, so a tab left open across midnight shows yesterday's
// number until any app state changes. A day-granular countdown at most a day
// stale on an idle tab is within tolerance, and re-fetching the session — which
// the add-card flow does — recomputes it.
const selectTrialDaysRemaining = () => createSelector(
  selectSessionUserLoaded(),
  selectUser(),
  (sessionLoaded, user) => {
    if (!sessionLoaded) return null;

    // `trial_end_date` is the ONE server-computed trial end. rails spans every
    // trial shape behind it — a self-serve 7-day software trial, a 30-day
    // hardware-purchase trial, a Stripe `trialing` subscription, an
    // explicitly-dated trial — and returns null when the account is not on a
    // trial. All trial-type detection lives on the server (a hardware trial,
    // for one, is indistinguishable from a paid subscription from the session
    // fields alone); the client only counts days to the date it is handed.
    const endDate = user.trial_end_date;
    // `dayjs(undefined)` is NOW — a valid date — so an absent/null field would
    // otherwise read as a trial ending today rather than as "not on a trial".
    if (typeof endDate !== 'string' || endDate === '') return null;

    const end = dayjs(endDate);
    if (!end.isValid()) return null;

    const days = end.startOf('day').diff(dayjs().startOf('day'), 'day');
    // A trial already past its end date is Stripe's to resolve, not ours to
    // narrate; the status will follow. Never render a negative countdown.
    return days >= 0 ? days : null;
  }
);

// Whether to offer this account the Stripe billing portal.
//
// Read fail-OPEN, and for the same reason `selectChannelManagementBlocked`
// above is read fail-CLOSED: the two answer different questions about an absent
// field. movebooth-rails has served `stripe_customer_id` on the session since
// rails #1224; ABSENT therefore means an older backend that cannot tell us —
// and hiding the only route to billing from every customer, because such a
// backend does not volunteer the id, would be the worse failure. An explicit
// `null` or empty string is different: that is the server saying this account
// has no Stripe customer behind it (a rental, comped, or manually-invoiced
// account), and there the button is hidden, because it could only ever fail.
//
// The endpoint is the real authority either way; this only decides what the
// customer is shown.
const selectHasBillingPortal = () => createSelector(
  selectSessionUserLoaded(),
  selectUser(),
  (sessionLoaded, user) => {
    if (!sessionLoaded) return false;
    if (!('stripe_customer_id' in user)) return true;
    return Boolean(user.stripe_customer_id);
  }
);

/**
 * Default selector used by App
 */

const selectApp = () => createSelector(
  selectAppDomain(),
  (substate) => substate
);

export default selectApp;
export {
  selectAppDomain,
  selectUser,
  selectAuthToken,
  selectEmail,
  selectIsPhotographer,
  selectPermissionRole,
  selectEnterpriseAdmins,
  selectChannelManagementBlocked,
  selectSessionUserLoaded,
  selectSessionLoadFailed,
  selectTrialDaysRemaining,
  selectHasBillingPortal
};
